import { supporterSalesQuery } from "@/api/reporting";
import { DataTable } from "@/components/data-table/DataTable";
import { DataTableBody } from "@/components/data-table/DataTableBody";
import { DataTableHeader } from "@/components/data-table/DataTableHeader";
import { StyledLink } from "@/components/link/StyledLink";
import { PointsAmount } from "@/components/PointsAmount";
import { Button } from "@/components/ui/Button";
import { ButtonBar } from "@/components/ui/ButtonBar";
import { DateRangePicker } from "@/components/ui/DateRangePicker";
import { PageWrapper } from "@/components/ui/PageWrapper";
import { countryCodeToTokenCurrencyCode } from "@/modules/money-fns";
import { firstDayOfMonth, lastDayOfMonth } from "@/utils/date";
import { toDateOnly } from "@common/utils/date";
import { Box, QueryHandler, Span, Text } from "@givenwell/components";
import { SupporterSalesDetail } from "@givenwell/management-api";
import { colors } from "@givenwell/theme";
import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { createFileRoute, getRouteApi, useNavigate } from "@tanstack/react-router";
import { fallback, object, optional, pipe, string, transform } from "valibot";

const route = getRouteApi("/admin/reports/$countryCode/supporter-sales");

const DEFAULT_FROM = firstDayOfMonth(new Date());
const DEFAULT_TO = lastDayOfMonth(new Date());

type SupporterSalesViews = "details" | "by_supplier" | "by_supporter";

function optionalStringToOptionalDate(dateStr: string = "") {
  const date = new Date(dateStr);
  if (!isNaN(date.getTime())) {
    return date;
  }
  return undefined;
}

export const Route = createFileRoute("/admin/reports/$countryCode/supporter-sales")({
  component: SupporterSalesReportPage,
  validateSearch: object({
    from: fallback(pipe(optional(string()), transform(optionalStringToOptionalDate)), undefined),
    to: fallback(pipe(optional(string()), transform(optionalStringToOptionalDate)), undefined),
    supplier_id: fallback(optional(string()), ""),
    supporter_id: fallback(optional(string()), ""),
    view: fallback(string(), "details"),
  }),
  beforeLoad: () => ({
    breadcrumb: "Supporter — Sales",
  }),
});

function SupporterSalesReportPage() {
  const search = route.useSearch();
  const { from, to, view: viewRaw, supplier_id, supporter_id } = search;
  const view = viewRaw as SupporterSalesViews;

  const fromAsDateOnly = toDateOnly(from ?? DEFAULT_FROM);
  const toAsDateOnly = toDateOnly(to ?? DEFAULT_TO);
  const query = useQuery(supporterSalesQuery(fromAsDateOnly, toAsDateOnly, supplier_id, supporter_id));
  const navigate = useNavigate();

  const cleanedSearch = { ...search, from: fromAsDateOnly, to: toAsDateOnly };

  function handleViewChange(value: SupporterSalesViews | undefined) {
    if (!value) return;

    navigate({
      to: ".",
      params: true,
      search: {
        ...cleanedSearch,
        view: value as string,
        supplier_id: value === "details" ? supplier_id : undefined,
        supporter_id: value === "details" ? supporter_id : undefined,
      },
    });
  }

  function handleClearFilters() {
    navigate({
      to: ".",
      params: true,
      search: {
        ...cleanedSearch,
        supplier_id: undefined,
        supporter_id: undefined,
      },
    });
  }

  return (
    <PageWrapper title="Supporter — Sales">
      <Box css={{ spaceY: 16, marginTop: 16 }}>
        <DataTable>
          <DataTableHeader hideSearch hideToggle hideClear>
            <DateRangePicker
              placeholder="Purchase date"
              value={{ start: from, end: to }}
              onValueChange={({ start, end }) => {
                navigate({
                  to: ".",
                  params: true,
                  search: {
                    ...search,
                    from: start!.toISOString(),
                    to: end!.toISOString(),
                  },
                });
              }}
            />
            <Span css={{ display: "flex", alignItems: "center", gap: 6, ml: 16 }}>
              <Text css={{ fontSize: "small" }}>View:</Text>
              <ButtonBar
                names={["Details", "By Supplier", "By Supporter"]}
                options={["details", "by_supplier", "by_supporter"] as SupporterSalesViews[]}
                value={view}
                onChange={handleViewChange}
              />
            </Span>
            {view === "details" && (!!supplier_id || !!supporter_id) && (
              <Button variant="outlined" onClick={handleClearFilters} size="sm">
                Clear filters
              </Button>
            )}
          </DataTableHeader>
          {view === "details" ? (
            <SupporterSalesDetailsTableBody query={query} search={cleanedSearch} />
          ) : view === "by_supplier" ? (
            <SupporterSalesBySupplierTableBody query={query} search={cleanedSearch} />
          ) : (
            <SupporterSalesBySupporterTableBody query={query} search={cleanedSearch} />
          )}
        </DataTable>
      </Box>
    </PageWrapper>
  );
}

type SupplierSummaryRow = {
  supplier_id: string;
  supplier_name: string;
  token_amount: number;
  num_sales: number;
};

type TableBodyProps = { query: UseQueryResult<SupporterSalesDetail[]>; search: any };

function SupporterSalesBySupplierTableBody({ query, search }: TableBodyProps) {
  const { countryCode } = route.useParams();
  const tokenCurrency = countryCodeToTokenCurrencyCode(countryCode) ?? "nztoken";
  return (
    <QueryHandler
      query={query}
      success={({ data }) => {
        const summary: SupplierSummaryRow[] = data.reduce((acc, sale) => {
          const existing = acc.find(row => row.supplier_id === sale.supplier_id);

          if (existing) {
            existing.token_amount += sale.token_amount;
            existing.num_sales += 1;
          } else {
            acc.push({
              supplier_id: sale.supplier_id,
              supplier_name: sale.supplier_name,
              token_amount: sale.token_amount,
              num_sales: 1,
            });
          }

          return acc;
        }, [] as SupplierSummaryRow[]);

        // Sort by token amount (DESC), then by supplier name (ASC)
        summary.sort((a, b) => {
          if (a.token_amount === b.token_amount) {
            return a.supplier_name.localeCompare(b.supplier_name);
          }

          return b.num_sales - a.num_sales;
        });

        return (
          <DataTableBody
            data={summary}
            dataKey="supplier_id"
            status="success"
            rounded="bottom"
            columns={[
              {
                header: "Supplier",
                cell: row => (
                  <StyledLink
                    to="/admin/reports/$countryCode/supporter-sales"
                    params={{ countryCode }}
                    search={{ ...search, supplier_id: row.supplier_id, view: "details" }}
                  >
                    {row.supplier_name}
                  </StyledLink>
                ),
              },
              {
                header: "Sales",
                cell: "num_sales",
                formatter: "number",
              },
              {
                header: "Points",
                cell: row => <PointsAmount amount={row.token_amount} tokenCurrency={tokenCurrency} />,
                css: { textAlign: "right" },
              },
            ]}
          />
        );
      }}
    />
  );
}

type SupporterSummaryRow = {
  supporter_id: string;
  supporter_name: string;
  token_amount: number;
  num_sales: number;
};

function SupporterSalesBySupporterTableBody({ query, search }: TableBodyProps) {
  const { countryCode } = route.useParams();
  const tokenCurrency = countryCodeToTokenCurrencyCode(countryCode) ?? "nztoken";

  return (
    <QueryHandler
      query={query}
      success={({ data }) => {
        const summary: SupporterSummaryRow[] = data.reduce((acc, sale) => {
          const existing = acc.find(row => row.supporter_id === sale.supporter_id);

          if (existing) {
            existing.token_amount += sale.token_amount;
            existing.num_sales += 1;
          } else {
            acc.push({
              supporter_id: sale.supporter_id,
              supporter_name: sale.supporter_name,
              token_amount: sale.token_amount,
              num_sales: 1,
            });
          }

          return acc;
        }, [] as SupporterSummaryRow[]);

        // Sort by token amount (DESC), then by supporter name (ASC)
        summary.sort((a, b) => {
          if (a.token_amount === b.token_amount) {
            return a.supporter_name.localeCompare(b.supporter_name);
          }

          return b.num_sales - a.num_sales;
        });

        return (
          <DataTableBody
            data={summary}
            dataKey="supporter_id"
            status="success"
            rounded="bottom"
            columns={[
              {
                header: "Supporter",
                cell: row => (
                  <StyledLink
                    to="/admin/reports/$countryCode/supporter-sales"
                    params={{ countryCode }}
                    search={{ ...search, supporter_id: row.supporter_id, view: "details" }}
                  >
                    {row.supporter_name}
                  </StyledLink>
                ),
              },
              {
                header: "Sales",
                cell: "num_sales",
                formatter: "number",
              },
              {
                header: "Points",
                cell: row => <PointsAmount amount={row.token_amount} tokenCurrency={tokenCurrency} />,
                css: { textAlign: "right" },
              },
            ]}
          />
        );
      }}
    />
  );
}

function SupporterSalesDetailsTableBody({ query, search }: TableBodyProps) {
  const { countryCode } = route.useParams();
  const tokenCurrency = countryCodeToTokenCurrencyCode(countryCode) ?? "nztoken";

  return (
    <DataTableBody
      data={query.data ?? []}
      dataKey="purchase_id"
      status={query.status}
      rounded="bottom"
      columns={[
        {
          header: "Purchase Date",
          cell: "purchase_date",
          formatter: "date",
          css: { width: 80 },
        },
        {
          header: "Time",
          cell: "purchase_date",
          formatter: "time",
          css: { width: 40 },
        },
        {
          header: "Supporter",
          cell: row => (
            <StyledLink
              to="/admin/reports/$countryCode/supporter-sales"
              params={{ countryCode }}
              search={{ ...search, supporter_id: row.supporter_id, view: "details" }}
              css={search.supporter_id && { backgroundColor: colors.yellow100 }}
            >
              {row.supporter_name}
            </StyledLink>
          ),
        },
        {
          header: "Supplier",
          cell: row => (
            <StyledLink
              to="/admin/reports/$countryCode/supporter-sales"
              params={{ countryCode }}
              search={{ ...search, supplier_id: row.supplier_id, view: "details" }}
              css={search.supplier_id && { backgroundColor: colors.yellow100 }}
            >
              {row.supplier_name}
            </StyledLink>
          ),
        },
        {
          header: "Listing",
          cell: "listing",
        },
        {
          header: "Points",
          cell: row => <PointsAmount amount={row.token_amount} tokenCurrency={tokenCurrency} />,
          css: { textAlign: "right" },
        },
      ]}
    />
  );
}
